import { useId, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import { FieldProvider } from './FieldContext';
import styles from './Field.module.scss';

export interface FieldProps {
  /** Подпись поля. Обязательна: поле без метки непригодно для скринридера. */
  label: ReactNode;
  children: ReactNode;
  /** Поясняющий текст под полем. Скрывается, когда показана ошибка. */
  hint?: ReactNode;
  /**
   * Текст ошибки. Присутствие ошибки само переводит поле в состояние
   * `invalid` — отдельного флага нет, чтобы они не разъехались.
   */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Спрятать метку визуально, оставив её для скринридера. */
  labelHidden?: boolean;
  className?: string;
}

/**
 * Обёртка поля: метка, подсказка, ошибка и связи между ними.
 *
 * Всё, что относится к любому элементу управления, живёт здесь один раз.
 * Сам элемент — `Input`, `Textarea` и то, что появится дальше, — забирает
 * идентификатор и признаки из контекста.
 */
export function Field({
  label,
  children,
  hint,
  error,
  required = false,
  disabled = false,
  labelHidden = false,
  className,
}: FieldProps) {
  const id = useId();
  const controlId = `${id}-control`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const invalid = error !== undefined && error !== null && error !== false;

  // Ошибка вытесняет подсказку: показывать оба сообщения разом — значит
  // заставлять человека читать инструкцию в тот момент, когда он уже
  // ошибся и ждёт, что именно исправить.
  const describedBy = invalid ? errorId : hint !== undefined ? hintId : undefined;

  return (
    <div className={cx(styles['field'], disabled && styles['field--disabled'], className)}>
      <label
        htmlFor={controlId}
        className={cx(styles['field__label'], labelHidden && styles['field__label--hidden'])}
      >
        {label}
        {required && (
          <span className={styles['field__required']} aria-hidden="true">
            *
          </span>
        )}
      </label>

      <FieldProvider value={{ controlId, describedBy, invalid, required, disabled }}>
        {children}
      </FieldProvider>

      {invalid ? (
        // role="alert" — чтобы ошибка, появившаяся после отправки формы,
        // была озвучена, а не осталась немой правкой разметки.
        <p id={errorId} className={styles['field__error']} role="alert">
          {error}
        </p>
      ) : (
        hint !== undefined && (
          <p id={hintId} className={styles['field__hint']}>
            {hint}
          </p>
        )
      )}
    </div>
  );
}
